import React, { useContext } from "react";
import { faBirthdayCake, faUserCheck, faUserClock, faUserXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Grid, Tooltip, Typography, useTheme } from "@mui/material";
import { GridColumns } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { RefModel } from "../../../../models/base/ref-model";
import { EmployeeRef } from "../../../../models/employees";
import { TnAOverviewsFilter } from "../../../../models/tna-overviews/tna-overview.filter";
import ConcatenatedCell from "../../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import { FT_MTPAL } from "../../../../utils/constants/filter-tables-constants";
import Table from "../../../../reusable-components/Table/Table";
import { MyTnAContext } from "../../MyTeamPresenceAndLeaves";
import { EmployeeNameContainer } from "../styles";
import { TnAWorkedTimesApiService } from "../../../../services/tna-worked-times/tna-worked-times-api.service";
import { useSearchParams } from "react-router-dom";
import { fromEmailParamsName } from "../../../NavigateToLoginPage/NavigateToLoginPage";
import EmailInfoPopover from "../../../EmailInfoPopover/EmailInfoPopover";
import { getDayAndMonth, getDaysInMonth, intlFormatDateShort } from "../../../../utils/time-utils";
import { TnADayDataRef } from "../../../../models/tna/tna-data/tna-day-data-ref";
import { TnAWorkedTimeDTO } from "../../../../models/tna-worked-time";
import { DIGEST_NOTIFICATION_TYPES } from "../../../../utils/constants/constants";
import { AUTHORIZATIONS } from "../../../../utils/constants/authorization-constants";
import { isAuthorizedEeGroup } from "../../../../custom-hooks/useAuthorization/useAuthorization";
import { useAppSelector } from "../../../../custom-hooks/redux/redux";
import { AbsencesContainer, ClientIconContainer, StatusIndicator } from "../../TnACalendar/styles";
import { TnAAbsenceDTO } from "../../../../models/tna";
import EditAbsenceWrapper from "../../TnACalendar/EditAbsenceWrapper/EditAbsenceWrapper";
import { LightTooltip } from "../../../AttachedDocuments/ButtonWithBadge/CustomTooltip/CustomTooltip";
import useForceUpdate from "src/custom-hooks/useForceUpdate/useForceUpdate";

interface Props {
  navigateToTimesheet: (employee: EmployeeRef | null) => void;
  quickfilters: (items: RefModel[], loading: boolean) => JSX.Element;
  getAllItems: ((items: RefModel[]) => void) | undefined;
}

const WorkedTimeTableView = ({ navigateToTimesheet, quickfilters, getAllItems }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.team_presence_and_leaves_worked_time";
  const translationFields = `${tRP}.fields`;
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";
  const [searchParams] = useSearchParams();
  const fromEmail = searchParams.get(fromEmailParamsName);
  const highlightedValue = searchParams.get("hl");
  const theme = useTheme();
  const { forceUpdate } = useForceUpdate();

  const { currentUserAuthorizations, currentUserNotificationPreferences } = useAppSelector(
    (state) => state.currentUserReducer
  );

  const {
    legalEntity: { legalEntityOption },
    employeeGroup: { employeeGroupOption },
    currentEmployees: { onlyCurrentEmployeesOptionValue },
    searchingDate: { year, month },
  } = useContext(MyTnAContext);

  const columns: GridColumns = [
    {
      field: "employee.employeeCode, employee.internalId",
      renderHeader: () => (
        <Typography>{`${t(`${tRPCommon}.employee_code`)} | ${t(`${tRPCommon}.internal_id`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 100,
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"center"}
          FirstElement={
            <Tooltip title={`${t(`${tRPCommon}.employee_code`)}`}>
              <Typography>{row.employee?.employeeCode ?? ""}</Typography>
            </Tooltip>
          }
          SecondElement={
            <Tooltip title={`${t(`${tRPCommon}.internal_id`)}`}>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
              >
                {row.employee?.internalId ?? ""}
              </Typography>
            </Tooltip>
          }
        />
      ),
    },
    {
      field: "employee.name",
      renderCell: ({ row }) => (
        <ConcatenatedCell
          aligned={"left"}
          FirstElement={
            <EmployeeNameContainer>
              <Grid sx={{ display: "flex", flexDirection: "column" }}>
                <span
                  title={row?.employee?.name}
                  onClick={() => {
                    navigateToTimesheet(row.employee);
                  }}
                >
                  {row?.employee?.name}
                </span>
                {row?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(row.employee.birthDate)}
                  </Typography>
                )}
              </Grid>
            </EmployeeNameContainer>
          }
          SecondElement={
            <Grid>
              <Typography
                style={{ color: theme.palette.text.disabled }}
                variant={"body2"}
                title={row?.employee?.legalEntity?.name}
              >
                {row?.employee?.legalEntity?.code ?? row?.employee?.legalEntity?.name}
              </Typography>
            </Grid>
          }
        />
      ),
      renderHeader: () => (
        <Typography>{`${t(`common_words.employee`)} | ${t(`common_words.legal_entity`)}`}</Typography>
      ),
      headerAlign: "center",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "employee.tnAGroup",
      renderHeader: () => <Typography>{`${t(`common_words.tna_employee_group`)}`}</Typography>,
      headerAlign: "center",
      flex: 0.6,
      minWidth: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: ({ row }) => (
        <Tooltip title={row.employee?.tnAGroup?.name}>
          <Typography
            key={row.employee?.tnAGroup?.id}
            sx={{ margin: "0 5px" }}
          >
            {row.employee?.tnAGroup?.code}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "workedTime",
      renderCell: ({ row }) => {
        return `${!!row?.workedTime ? row?.workedTime.toFixed(2) : "0.00"}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.total_worked_this_month`)}`}</Typography>,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "workedTimeBalance",
      renderCell: ({ row }) => {
        return `${row?.workedTimeBalance ? row?.workedTimeBalance.toFixed(2) : "0.00"}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.current_due_vs_worked_time`)}`}</Typography>,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 110,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "workedVsDueCorrections",
      renderCell: ({ row }) => {
        return `${row?.dueVSWorkedCorrections ? row?.dueVSWorkedCorrections.toFixed(2) : "0.00"}`;
      },
      renderHeader: () => <Typography>{`${t(`${translationFields}.worked_vs_due_corrections`)}`}</Typography>,
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 110,
      sortable: true,
      disableColumnMenu: true,
    },
    {
      field: "totalWorkedTimeBalance",
      renderCell: ({ row }) => {
        return `${!!row?.totalWorkedTimeBalance ? row?.totalWorkedTimeBalance.toFixed(2) : "0.00"}`;
      },
      // headerName: t(`${translationFields}.total_balance`),
      renderHeader: () => (
        <>
          <Typography>{t(`${translationFields}.total_due_vs_worked_balance`)}</Typography>
          {fromEmail && (
            <EmailInfoPopover
              autoOpen={true}
              message={"Only values of employees for whom you are an approver are highlighted"}
            />
          )}
        </>
      ),
      headerAlign: "center",
      align: "center",
      flex: 1,
      minWidth: 100,
      sortable: true,
      disableColumnMenu: true,
      cellClassName: (params) =>
        fromEmail && highlightedValue === "exceeding-time" && highlightCell(params.row) ? "highlighted" : "",
    },
  ];

  const pinnedColumns = {
    left: [
      "employee.employeeCode, employee.internalId",
      "employee.name",
      "employee.tnAGroup",
      "workedTime",
      "workedTimeBalance",
      "workedVsDueCorrection",
      "totalWorkedTimeBalance",
    ],
  };

  const dynamicColumns = (year: number, month: number) => {
    let result: GridColumns = [];
    const daysInMonth = getDaysInMonth(year, month);
    for (let i = 0; i < daysInMonth; i++) {
      result.push({
        field: new Date(year, month, i + 1).toString(),
        renderCell: ({ row }) => {
          const currDay = row?.daysData?.find(
            (day: TnADayDataRef) =>
              day?.tnADate?.day === i + 1 && day.tnADate.month === month + 1 && day.tnADate.year === year
          );

          const isWeekend = [0, 6].indexOf(new Date(year, month, i + 1).getDay()) !== -1;

          if (isWeekend) return null;

          if (currDay?.absences?.length) {
            return (
              <AbsencesContainer>
                {currDay?.absences.map((abs: TnAAbsenceDTO) => {
                  return (
                    <StatusIndicator
                      isRejected={abs.isRejected}
                      isCancelled={abs.isCancelled}
                      isApproved={abs.isApproved}
                      key={abs.id}
                    >
                      <EditAbsenceWrapper
                        absence={abs}
                        employee={row.employee}
                        handleNotifyParent={forceUpdate}
                        allowedToApproveReject
                        viewMode={false}
                        year={year}
                        month={month}
                        forManager
                        allowedOnlyManagersAction
                      >
                        <ClientIconContainer>
                          <LightTooltip
                            title={
                              <Grid>
                                <Typography>
                                  Created by: <b>{abs.createdByUser?.name ?? abs.createdByUser?.email ?? ""}</b>
                                </Typography>

                                {abs.modifiedByUser && (abs.isApproved || abs.isRejected) && !abs.isCancelled && (
                                  <>
                                    {abs.isApproved && (
                                      <Typography>
                                        Approved by: <b>{abs.modifiedByUser?.name ?? ""}</b>
                                      </Typography>
                                    )}

                                    {abs.isRejected && (
                                      <Typography>
                                        Rejected by: <b>{abs.modifiedByUser?.name ?? ""}</b>
                                      </Typography>
                                    )}
                                  </>
                                )}
                              </Grid>
                            }
                          >
                            <Typography sx={{ fontSize: "0.7rem", lineHeight: "2em" }}>
                              {`${abs.absenceType?.code}${
                                !abs.isRejected && !abs.isCancelled && !abs.isApproved ? " ?" : ""
                              }`}
                            </Typography>
                          </LightTooltip>

                          {!!abs.countedDurationDays && (
                            <Typography
                              variant="body2"
                              title={`Counted days :  ${abs.countedDurationDays} day${
                                abs.countedDurationDays > 1 ? "s" : ""
                              }/ Total absence days : ${abs.calendaristicDays} day${
                                abs.calendaristicDays > 1 ? "s" : ""
                              }`}
                            >{` ${abs.countedDurationDays} day${abs.countedDurationDays > 1 ? "s" : ""} `}</Typography>
                          )}

                          <React.Fragment>
                            {abs?.needAcceptance &&
                              (abs?.acceptedByClient ? (
                                <FontAwesomeIcon
                                  icon={faUserCheck}
                                  color={"greenyellow"}
                                  title={`Accepted by client ${
                                    abs.acceptedRejectedByUser?.name ?? abs.acceptedRejectedByEmail
                                  }`}
                                />
                              ) : abs?.rejectedByClient ? (
                                <FontAwesomeIcon
                                  icon={faUserXmark}
                                  color={theme.palette.error.main}
                                  title={`Rejected by client ${
                                    abs.acceptedRejectedByUser?.name ?? abs.acceptedRejectedByEmail
                                  }`}
                                />
                              ) : (
                                <FontAwesomeIcon
                                  icon={faUserClock}
                                  color={theme.palette.text.secondary}
                                  title={"Pending client acceptance"}
                                />
                              ))}
                          </React.Fragment>
                        </ClientIconContainer>
                      </EditAbsenceWrapper>
                    </StatusIndicator>
                  );
                })}
              </AbsencesContainer>
            );
          }

          return !isWeekend && <span>{currDay?.presence.toFixed(2)}</span>;
        },
        headerName: getDayAndMonth(new Date(year, month, i + 1)),
        headerAlign: "center",
        align: "center",
        flex: 0.5,
        minWidth: 80,
        sortable: false,
        disableColumnMenu: true,
        cellClassName: (params: any) => {
          const currDay = new Date(params.field);
          const dayOfWeek: number = currDay.getDay();
          const thisDayData = params.row?.daysData?.find(
            (day: TnADayDataRef) => day?.tnADate?.day === currDay.getDate()
          );

          const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;

          return thisDayData?.isPublicHoliday ? "isPublicHoliday" : isWeekend ? "isWeekend" : "";
        },
      });
    }

    return result;
  };

  const highlightCell = (row: TnAWorkedTimeDTO) => {
    const tnaGroupId = row.employee?.tnAGroup?.id;
    const isApproverForThisEe =
      !!tnaGroupId &&
      isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        [tnaGroupId],
        true
      );

    if (isApproverForThisEe) {
      const notification = currentUserNotificationPreferences.find(
        (notif) => notif.notificationType?.id === DIGEST_NOTIFICATION_TYPES.my_team_timesheet.id
      );
      const threshold = notification?.digestSectionsParams.find(
        (section) =>
          section.digestSection?.id === DIGEST_NOTIFICATION_TYPES.my_team_timesheet.sections.DueVSActualWorkedTimeId
      )?.value;
      if (threshold) {
        return row.totalWorkedTimeBalance >= threshold;
      }
      return false;
    }
    return false;
  };

  const defaultItem: Partial<TnAWorkedTimeDTO> = {
    employee: null,
    daysData: [],
  };

  return (
    <Table
      displayTitle={false}
      translationRootPath={tRP}
      pinnedColumns={pinnedColumns}
      columns={columns}
      serviceApi={new TnAWorkedTimesApiService()}
      propsFilter={
        {
          ...legalEntityOption,
          ...employeeGroupOption,
          ...onlyCurrentEmployeesOptionValue,
          year,
          month: month + 1,
        } as TnAOverviewsFilter
      }
      defaultItem={defaultItem}
      filter={FT_MTPAL}
      readOnly
      quickFilters={quickfilters}
      dynamicColumns={dynamicColumns(year, month)}
      getAllItems={getAllItems}
    />
  );
};

export default WorkedTimeTableView;
